import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { logger } from "@/lib/logger";

// Note: only stores non-sensitive notification IDs (UUIDs) for de-duplication
// across page reloads. No tokens, content, or PII is ever written here.
const SEEN_KEY = "senrizeflow:seen_notification_ids";

function loadSeen() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    // localStorage disabled (private mode / quota) — degrade silently
    return new Set();
  }
}

function saveSeen(set) {
  try {
    // Cap to last 200 ids to keep localStorage tiny
    const arr = Array.from(set).slice(-200);
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch {
    // localStorage disabled — non-fatal, dedup just won't persist across reloads
  }
}

/**
 * Real-time notification poller.
 * - Polls /notifications every `intervalMs` (default 15s) for fresh items
 * - Fires an in-app sonner toast (top-right) for each new item
 * - Also fires a native desktop Notification when permission is granted
 * - Idempotent across reloads via localStorage seen-IDs
 * - Optional `onUpdate(unreadCount)` callback fires every tick so external
 *   UI (bell badge) can stay in sync without a separate poll loop.
 */
export default function useDesktopNotifications({
  enabled = true,
  intervalMs = 15000,
  onUpdate,
} = {}) {
  const seenRef = useRef(loadSeen());
  const firstRunRef = useRef(true);
  const onUpdateRef = useRef(onUpdate);
  // Keep latest callback without resetting the polling effect
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    let active = true;

    const askPermission = () => {
      if (!("Notification" in window)) return;
      if (Notification.permission === "default") {
        try {
          Notification.requestPermission().catch(() => {
            // user dismissed or browser policy blocked — non-fatal
          });
        } catch {
          // older Safari throws on the legacy callback signature — non-fatal
        }
      }
    };
    askPermission();

    const fireDesktop = (n) => {
      if (!("Notification" in window)) return;
      if (Notification.permission !== "granted") return;
      try {
        const notif = new Notification(n.title || "SenrizeFlow", {
          body: n.body || "",
          icon: "/favicon.ico",
          badge: "/favicon.ico",
          tag: n.id,
          silent: false,
        });
        if (n.link) {
          notif.onclick = () => {
            window.focus();
            try {
              window.location.assign(n.link);
            } catch {
              // navigation blocked (e.g. cross-origin) — close and bail
            }
            notif.close();
          };
        }
      } catch (err) {
        logger.error("desktop-notif fire failed:", err);
      }
    };

    const fireToast = (n) => {
      const opts = {
        id: `notif-${n.id}`,
        description: n.body || undefined,
        duration: 7000,
      };
      if (n.link) {
        opts.action = {
          label: "Open",
          onClick: () => {
            try {
              window.location.assign(n.link);
            } catch {
              // navigation blocked — toast already fired, nothing else to do
            }
          },
        };
      }
      const kind = (n.kind || "info").toLowerCase();
      const title = n.title || "Notification";
      if (kind === "success" || kind === "approved") toast.success(title, opts);
      else if (kind === "warning" || kind === "late" || kind === "overdue") toast.warning(title, opts);
      else if (kind === "error" || kind === "rejected") toast.error(title, opts);
      else toast(title, opts);
    };

    const tick = async () => {
      try {
        const { data } = await api.get("/notifications", { params: { unread_only: true } });
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        if (typeof onUpdateRef.current === "function") {
          try { onUpdateRef.current(list.length); } catch { /* ignore */ }
        }
        if (firstRunRef.current) {
          // On first run: mark all current unread as seen (don't spam on login/refresh)
          for (const n of list) seenRef.current.add(n.id);
          saveSeen(seenRef.current);
          firstRunRef.current = false;
          return;
        }
        // Newest first from API → reverse so older toasts queue first
        const fresh = list.filter((n) => !seenRef.current.has(n.id)).reverse();
        for (const n of fresh) {
          fireToast(n);
          fireDesktop(n);
          seenRef.current.add(n.id);
          // Iter182 — broadcast so any open page (e.g. Approvals)
          // can hot-refresh its data without waiting for its own
          // poll. Detail carries the full notification payload.
          try {
            window.dispatchEvent(
              new CustomEvent("senrizeflow:notification", { detail: n }),
            );
          } catch {
            // CustomEvent unsupported (very old browser) — non-fatal
          }
        }
        if (fresh.length) saveSeen(seenRef.current);
      } catch (err) {
        if (err?.response?.status !== 401) {
          logger.error("desktop-notif poll failed:", err);
        }
      }
    };

    tick();
    const t = setInterval(tick, intervalMs);
    return () => { active = false; clearInterval(t); };
  }, [enabled, intervalMs]);
}
